import { AlertTriangle, Check, Loader2, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAccordion, ColocaAccordionItem } from "@/shared/components/layout/ColocaAccordion";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ACCESS_AREAS, areaModules, type AccessArea } from "../../constants/accessAreas.constants";
import type { AccessCatalogModule } from "../../constants/accessCatalog.constants";
import type { AtomicAction } from "../../types/access.types";
import {
  AREA_LEVELS,
  cellKey,
  currentAreaLevel,
  type AreaLevel,
  type PermissionCell,
} from "../../utils/permissionCells";

/**
 * What one toggle shows. - `on` / `off`: ticked or not by the person editing. - `covered`: not ticked here,
 * but granted anyway by something else in the selection (a combined role). - `denied`: a selected deny policy
 * blocks it. - `unavailable`: nothing in the catalog grants exactly this, so there is nothing to tick.
 */
export type BoardCellState = "on" | "off" | "covered" | "denied" | "unavailable";

export interface PermissionBoardProps {
  cellState: (cell: PermissionCell) => BoardCellState;
  /** Omit for a read-only board. */
  onToggle?: (cell: PermissionCell) => void;
  /** Area shortcuts ("Solo ver", "Todo"…). Omit to hide them. */
  onApplyLevel?: (area: AccessArea, level: AreaLevel) => void;
  /** The cell (`cellKey`) or area (`area:<id>`) whose save is in flight — it shows the wait. */
  pendingKey?: string | null;
  /** Every control inert, e.g. while any save is in flight. */
  busy?: boolean;
  /** Deny paints the ticked cells red: the board is then choosing what to block. */
  tone?: "allow" | "deny";
  /** Filters modules by their label or id; matching areas are forced open. */
  query?: string;
  className?: string;
}

const isGranted = (state: BoardCellState) => state === "on" || state === "covered";

/**
 * The catalog as eight business areas, each a collapsible panel of module rows with a toggle per action (Ver
 * / Editar / Eliminar). Replaces lists of ~90 one-permission rows: the person picks "what can they do with
 * Cards", not "Cards Read" out of a long scroll.
 *
 * The board knows nothing about roles or policies — the parent maps each cell to whatever stands for it and
 * reports the state back. That is what lets the role editor, the policy editor, the assignment drawer and the
 * invitation share one picker.
 */
export function PermissionBoard({
  cellState,
  onToggle,
  onApplyLevel,
  pendingKey = null,
  busy = false,
  tone = "allow",
  query = "",
  className,
}: PermissionBoardProps) {
  const { t } = useTranslations("settings");
  const readOnly = !onToggle;

  // Until the person touches an area, it is open exactly when something in it is granted — derived, not
  // seeded, because parents fill their selection in an effect after the first render. The first click in an
  // area (header, toggle or shortcut) pins its state, so a panel never folds under the click that emptied it.
  // Parents remount the board (`key`) for a new subject.
  const [openAreas, setOpenAreas] = useState<Record<string, boolean>>({});
  const pinOpen = (areaId: string) =>
    setOpenAreas((prev) => (prev[areaId] ? prev : { ...prev, [areaId]: true }));

  const term = query.trim().toLowerCase();

  const areas = useMemo(
    () =>
      ACCESS_AREAS.map((area) => {
        const all = areaModules(area);
        const areaMatches = !term || t(area.labelKey).toLowerCase().includes(term);
        const modules = areaMatches
          ? all
          : all.filter((mod) => t(mod.labelKey).toLowerCase().includes(term) || mod.id.includes(term));
        return { area, modules, all };
      }).filter(({ modules }) => modules.length > 0),
    [term, t],
  );

  if (areas.length === 0) {
    return (
      <div className="flex items-center gap-2 px-0.5 py-3 text-[12px] text-muted-foreground">
        <Search size={13} aria-hidden />
        {t("access_board_no_matches")}
      </div>
    );
  }

  return (
    <ColocaAccordion className={className}>
      {areas.map(({ area, modules, all }) => {
        const total = all.reduce((n, mod) => n + mod.actions.length, 0);
        const granted = all.reduce(
          (n, mod) =>
            n + mod.actions.filter((atom) => isGranted(cellState({ moduleId: mod.id, atom }))).length,
          0,
        );
        const level = onApplyLevel
          ? // What is granted counts, whoever grants it: an area a combined role already opens must not read
            // as "Nothing" just because none of its own toggles is ticked.
            currentAreaLevel(
              area,
              (cell) => isGranted(cellState(cell)),
              (cell) => cellState(cell) !== "unavailable",
            )
          : null;
        const hasSensitive = all.some((mod) => mod.sensitive);
        const areaPending = pendingKey === `area:${area.id}`;
        const toggleInArea = onToggle
          ? (cell: PermissionCell) => {
              pinOpen(area.id);
              onToggle(cell);
            }
          : undefined;

        return (
          <ColocaAccordionItem
            key={area.id}
            title={t(area.labelKey)}
            icon={area.icon}
            open={term ? true : (openAreas[area.id] ?? granted > 0)}
            onOpenChange={(next) => {
              if (!term) setOpenAreas((prev) => ({ ...prev, [area.id]: next }));
            }}
            meta={
              <span
                className={cn(
                  "font-mono text-[10.5px] tabular-nums",
                  granted > 0 ? "text-primary" : "text-muted-foreground/70",
                )}
                aria-label={t("access_area_count_aria", { granted, total })}
              >
                {granted}/{total}
              </span>
            }
            contentClassName="p-0"
          >
            <div className="flex flex-col gap-2 border-b border-border/60 px-3 py-2.5">
              <p className="text-[11.5px] leading-snug text-muted-foreground">{t(area.hintKey)}</p>
              {onApplyLevel && !term && !readOnly && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-0.5 text-[11px] font-medium text-foreground">
                    {t("access_preset_label")}
                  </span>
                  {AREA_LEVELS.map((option) => {
                    const active = level === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        aria-pressed={active}
                        disabled={busy}
                        onClick={() => {
                          pinOpen(area.id);
                          onApplyLevel(area, option);
                        }}
                        className={cn(
                          "inline-flex min-h-7 items-center gap-1 rounded-full border px-2.5 text-[11.5px] transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          "disabled:cursor-not-allowed disabled:opacity-60",
                          active
                            ? tone === "deny"
                              ? "border-destructive bg-destructive/10 font-semibold text-destructive"
                              : "border-primary bg-primary/10 font-semibold text-primary"
                            : "border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                        )}
                      >
                        {areaPending && active && <Loader2 size={11} className="animate-spin" aria-hidden />}
                        {t(`access_preset_${option}`)}
                      </button>
                    );
                  })}
                  {areaPending && level === null && (
                    <Loader2 size={12} className="animate-spin text-muted-foreground" aria-hidden />
                  )}
                </div>
              )}
              {onApplyLevel && !term && !readOnly && hasSensitive && (
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <AlertTriangle size={11} className="shrink-0 text-warning" aria-hidden />
                  {t("access_preset_sensitive_note")}
                </p>
              )}
            </div>

            <ul className="m-0 list-none divide-y divide-border/50 p-0">
              {modules.map((mod) => (
                <ModuleRow
                  key={mod.id}
                  mod={mod}
                  cellState={cellState}
                  onToggle={toggleInArea}
                  pendingKey={pendingKey}
                  busy={busy}
                  tone={tone}
                />
              ))}
            </ul>
          </ColocaAccordionItem>
        );
      })}
    </ColocaAccordion>
  );
}

interface ModuleRowProps {
  mod: AccessCatalogModule;
  cellState: (cell: PermissionCell) => BoardCellState;
  onToggle?: (cell: PermissionCell) => void;
  pendingKey: string | null;
  busy: boolean;
  tone: "allow" | "deny";
}

function ModuleRow({ mod, cellState, onToggle, pendingKey, busy, tone }: ModuleRowProps) {
  const { t } = useTranslations("settings");
  const ModIcon = mod.icon;
  const label = t(mod.labelKey);

  return (
    <li className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-3 py-2">
      <span className="flex min-w-0 flex-1 basis-40 items-center gap-2">
        <ModIcon size={13} className="shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-[12.5px] font-medium text-foreground" title={mod.id}>
          {label}
        </span>
        {mod.sensitive && (
          <ColocaBadge variant="secondary" className="shrink-0 text-[9.5px]">
            {t("access_module_sensitive")}
          </ColocaBadge>
        )}
      </span>
      <span className="ml-auto flex shrink-0 gap-1">
        {mod.actions.map((atom) => (
          <ActionToggle
            key={atom}
            moduleId={mod.id}
            moduleLabel={label}
            atom={atom}
            risky={!!mod.risky?.[atom]}
            state={cellState({ moduleId: mod.id, atom })}
            pending={pendingKey === cellKey(mod.id, atom)}
            busy={busy}
            tone={tone}
            onToggle={onToggle}
          />
        ))}
      </span>
    </li>
  );
}

interface ActionToggleProps {
  moduleId: string;
  moduleLabel: string;
  atom: AtomicAction;
  risky: boolean;
  state: BoardCellState;
  pending: boolean;
  busy: boolean;
  tone: "allow" | "deny";
  onToggle?: (cell: PermissionCell) => void;
}

function ActionToggle({
  moduleId,
  moduleLabel,
  atom,
  risky,
  state,
  pending,
  busy,
  tone,
  onToggle,
}: ActionToggleProps) {
  const { t } = useTranslations("settings");
  const actionLabel = t(`access_level_${atom}`);
  const locked = state === "covered" || state === "denied" || state === "unavailable";
  const disabled = !onToggle || locked || busy;
  const on = state === "on";
  const denyTone = tone === "deny";

  const hint = [
    t(`access_level_${atom}_hint`),
    state === "covered" && t("access_cell_covered"),
    state === "denied" && t("access_cell_denied"),
    state === "unavailable" && t("access_cell_unavailable"),
    risky && t("access_cell_risky"),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on || state === "covered"}
      aria-label={`${moduleLabel}: ${actionLabel}`}
      title={hint}
      disabled={disabled}
      onClick={() => onToggle?.({ moduleId, atom })}
      className={cn(
        "inline-flex min-h-7 min-w-[4.25rem] items-center justify-center gap-1 rounded-md border px-2 text-[11.5px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        on &&
          (denyTone
            ? "border-destructive bg-destructive/10 font-semibold text-destructive"
            : "border-primary bg-primary/10 font-semibold text-primary"),
        state === "off" &&
          "border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground",
        state === "covered" && "border-primary/30 bg-primary/5 text-primary/80",
        state === "denied" && "border-destructive/40 bg-destructive/10 text-destructive",
        state === "unavailable" && "border-dashed border-border text-muted-foreground/40",
        disabled && !locked && "cursor-not-allowed opacity-60",
        locked && "cursor-default",
      )}
    >
      {pending ? (
        <Loader2 size={11} className="animate-spin" aria-hidden />
      ) : on || state === "covered" ? (
        denyTone ? (
          <X size={11} aria-hidden />
        ) : (
          <Check size={11} aria-hidden />
        )
      ) : state === "denied" ? (
        <X size={11} aria-hidden />
      ) : null}
      {actionLabel}
    </button>
  );
}
